import { TypeInitProps, TypeRequest } from '../../types';
import { encryptParams } from '../../utils/index';
import { WEB_URL } from '../../utils/urls';
import { get } from '../../utils/data';
export default class UserAPI {
  private request: TypeRequest;
  private props: TypeInitProps & { request: TypeRequest };
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request, aid } = props;
    this.request = request;
    this.props = props;
  }

  /**
   * @param {string} access_token - the third platform token
   * @param {string} access_token_secret - the google platform secret token
   * @param {string} code - the third platform code
   * @param {number} expires_in - the token expire time
   * @param {string} openid - the third platform openid
   * @param {string} platform - the third platform name
   * @param {number} platform_app_id - the third platform app_id
   * @return {Promise} Oauth Profile result
   */
  getOauthProfile = (params: {
    access_token?: string;
    access_token_secret?: string;
    code?: string;
    expires_in?: number;
    openid?: string;
    platform_app_id: number;
    platform: string;
    extra_params?: object;
  }) => {
    const {
      access_token,
      access_token_secret,
      code,
      expires_in,
      openid,
      platform_app_id,
      platform,
      extra_params = {},
    } = params;
    return this.request({
      url: WEB_URL.OAUTH_PROFILE,
      method: 'GET',
      data: {
        aid: this.props?.aid,
        access_token,
        access_token_secret,
        code,
        expires_in,
        openid,
        platform_app_id,
        platform,
        ...extra_params,
      },
    });
  };

  /**
   * @param {File} avatar - the user avatar to upload
   * @return {Promise} upload result
   */
  uploadAvatar = (params: { avatar: File; extra_params?: object }) => {
    const { avatar, extra_params } = params;
    let data = new FormData();
    let blob = new Blob([avatar], { type: avatar.type });
    data.append('avatar', blob);
    const url = WEB_URL.UPLOAD_AVATAR;
    return this.request({
      url,
      method: 'POST',
      data,
      params: {
        ...extra_params,
      },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  /**
   * @param {string} avatar - the user avatar
   * @param {string} name - the user name
   * @param {string} description - the user short desc
   * @param {string} area - the user area
   * @param {string} gender - the user gender
   * @param {string} birthday - the user birthday
   * @param {string} expend_attrs - the business custom params
   * @return {Promise} login result
   */
  updateUserInfo = (params: {
    avatar?: string;
    name?: string;
    description?: string;
    area?: string;
    gender?: string;
    birthday?: string;
    expend_attrs?: string;
    extra_params?: object;
  }) => {
    const { extra_params = {}, ...otherParams } = params;
    const data = { ...otherParams, ...extra_params };
    return this.request({
      url: WEB_URL.UPDATE_USERINFO,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} scene - the business scene
   * @return {Promise} check result
   */
  checkDefaultInfo = (params: { scene: string; extra_params?: object }) => {
    const { scene, extra_params = {} } = params;
    return this.request({
      url: WEB_URL.CHECK_DEFAULT_INFO,
      method: 'GET',
      params: {
        scene,
        ...extra_params,
      },
    });
  };

  /**
   * @return {Promise} get result
   */
  getDefaultImageList = (params: { extra_params?: object }) => {
    const { aid } = this.props;
    const { extra_params = {} } = params;
    const url = WEB_URL.GET_BACKGROUND_LIST.replace('{aid}', String(aid));
    return this.request({
      url,
      method: 'GET',
      params: {
        ...extra_params,
      },
    });
  };

  /**
   * @return {Promise} get avatar list result
   */
  getDefaultAvatarList = (params: { extra_params?: object }) => {
    const { aid } = this.props;
    const { extra_params = {} } = params;
    const url = WEB_URL.GET_DEFAULT_AVATAR_LIST.replace('{aid}', String(aid));
    return this.request({
      url: url,
      method: 'GET',
      params: {
        ...extra_params,
      },
    });
  };

  /**
   * @return {Promise} get recommend name result
   */
  getRecommendName = (params: { extra_params?: object }) => {
    const { extra_params = {} } = params;
    return this.request({
      url: WEB_URL.GET_RECOMMEND_NAME,
      method: 'GET',
      params: {
        ...extra_params,
      },
    });
  };

  /**
   * @param {string} field - the check key
   * @param {string} field_namespace - shark object
   * @return {Promise} check result
   */
  checkRemainTimes = (params: {
    field: string;
    field_namespace?: string;
    extra_params?: object;
  }) => {
    const { field, field_namespace = '', extra_params = {} } = params;
    return this.request({
      url: WEB_URL.CHECK_REMAIN_TIMES,
      method: 'GET',
      params: {
        field,
        field_namespace,
        ...extra_params,
      },
    });
  };

  /**
   * @param {string} pic - the image file
   * @param {boolean} is_sensitive - is sensitive
   * @return {Promise} result res: { message: ""; data: { web_uri : '' } }
   */
  uploadPicture = (params: {
    pic: File;
    is_sensitive?: boolean;
    extra_params?: object;
  }) => {
    const { pic, is_sensitive, extra_params = {} } = params;
    let data = new FormData();
    let blob = new Blob([pic], { type: pic.type });
    data.append('pic', blob);
    return this.request({
      url: WEB_URL.UPLOAD_PICTURE,
      method: 'POST',
      data,
      params: {
        is_sensitive,
        ...extra_params,
      },
    });
  };

  /**
   * @param {string} key - the image key
   * @return {Promise} login result
   */
  downloadImage = (params: { key: string; extra_params?: object }) => {
    const { key, extra_params = {} } = params;
    return this.request({
      url: WEB_URL.DOWNLOAD_IMAGE,
      method: 'GET',
      params: {
        key,
        ...extra_params,
      },
    });
  };
}
